import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { List, X, CaretDown } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { useBooking } from "@/context/BookingContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Projects", to: "/projects" },
  { label: "Amenities", to: "/amenities" },
  { label: "Gallery", to: "/gallery" },
  { label: "Updates", to: "/construction-updates" },
  { label: "Contact", to: "/contact" },
];

const MORE = [
  { label: "Buyer Account", to: "/buyer/login" },
  { label: "Partner Portal", to: "/partner/login" },
  { label: "EMI Calculator", to: "/emi" },
  { label: "Floor Plans", to: "/floor-plans" },
  { label: "Channel Partners", to: "/channel-partner" },
  { label: "Insights", to: "/blog" },
  { label: "FAQ", to: "/faq" },
  { label: "Careers", to: "/careers" },
];

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  const { open } = useBooking();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMobile(false), [location.pathname]);

  useEffect(() => {
    if (!mobile) return undefined;

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setMobile(false);
    };

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    document.addEventListener("keydown", closeOnEscape);

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [mobile]);

  return (
    <>
    <header
      className={`fixed inset-x-0 top-0 z-[9000] transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-black/70 border-b border-white/10" : "bg-gradient-to-b from-black/60 to-transparent"
      }`}
      data-testid="navbar"
    >
      <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-5 py-4 sm:px-8">
        <Link to="/" className="group flex items-center gap-2" data-testid="nav-logo">
          <span className="text-gold text-2xl">&#9670;</span>
          <span className="font-serif text-2xl font-medium tracking-tight text-white">Aureon<span className="text-gold">.</span></span>
        </Link>

        <div className="hidden items-center gap-7 lg:flex">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={`link-underline text-[13px] font-light uppercase tracking-[0.15em] transition-colors ${
                location.pathname === n.to ? "text-gold" : "text-white/80 hover:text-white"
              }`}
            >
              {n.label}
            </Link>
          ))}
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-1 text-[13px] font-light uppercase tracking-[0.15em] text-white/80 outline-none hover:text-white" data-testid="nav-more">
              More <CaretDown size={12} />
            </DropdownMenuTrigger>
            <DropdownMenuContent className="border-white/10 bg-ink-2 text-white">
              {MORE.map((m) => (
                <DropdownMenuItem key={m.to} asChild className="focus:bg-gold/20 focus:text-white">
                  <Link to={m.to} data-testid={`nav-more-${m.label.toLowerCase().replace(/\s/g, "-")}`}>{m.label}</Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => open("site_visit")}
            data-testid="nav-book-visit"
            className="hidden rounded-sm border border-gold bg-transparent px-6 py-3 text-[11px] font-medium uppercase tracking-[0.18em] text-gold transition-all duration-300 hover:bg-gold hover:text-ink md:inline-flex"
          >
            Book Site Visit
          </button>
          <button className="lg:hidden text-white" onClick={() => setMobile(true)} data-testid="nav-mobile-open" aria-label="Open menu">
            <List size={26} />
          </button>
        </div>
      </nav>
    </header>

      <AnimatePresence>
        {mobile && (
          <motion.div
            className="fixed inset-0 z-[11000] h-[100dvh] overflow-hidden bg-ink lg:hidden"
            initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
            data-testid="mobile-menu"
          >
            <div className="flex h-[52px] items-center justify-between border-b border-white/10 bg-ink px-4">
              <span className="font-serif text-xl text-white">Aureon<span className="text-gold">.</span></span>
              <button onClick={() => setMobile(false)} className="text-white" data-testid="nav-mobile-close" aria-label="Close menu"><X size={24} /></button>
            </div>
            <div className="flex h-[calc(100dvh-52px)] flex-col px-4 py-2.5">
              <div className="grid grid-cols-2 gap-x-3">
              {[...NAV, ...MORE].map((n, i) => (
                <motion.div key={n.to} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.025 * i }}>
                  <Link to={n.to} onClick={() => setMobile(false)} className="flex min-h-9 items-center border-b border-white/10 py-1.5 font-serif text-base font-light leading-tight text-white/90">
                    {n.label}
                  </Link>
                </motion.div>
              ))}
            </div>
              <button onClick={() => { setMobile(false); open("site_visit"); }} className="mt-3 rounded-sm border border-gold bg-gold py-2.5 text-[11px] font-medium uppercase tracking-[0.16em] text-ink">
                Book Site Visit
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
